import type { FileForProcessing, IOrder, ISamplePool } from '../../../../Model/index.ts'
import { canUseVisualisationOnOrder, hasOwnInsightLicense, hasSharedInsightLicense, isChatBeta } from '../../../../Model/index.ts'
import {
  useGetAuthenticatedUser,
  useGetIntermediatesStatusSelector,
  useGetReprocessingGrantSelector,
  useOrderNotices
} from '../../../../Flux/Selector/index.ts'
import type { OrderState } from '../../../../Util/OrderState.ts'
import { CloudArrowUpIcon } from '@heroicons/react/24/outline'
import Actions from '../../../../Flux/index.ts'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.ts'
import { useDispatch } from 'react-redux'
import { Environment } from '../../../../environment.ts'
import { tracingAxios } from '../../../../Repository/Sentry.ts'
import { useNavigate } from 'react-router-dom'
import { useConfirm } from '../../../Component/ConfirmProvider.tsx'
import type { Taxas } from '../../../../Model/ITaxa.ts'
import {
  gCanBeResubmitted,
  gIsCustomer,
  gIsEDNA,
  gIsLab,
  gIsLabOrCustomer,
  gIsMetabarcoding,
  gIsUnsubmitted,
  gKronaAccess,
  gNeedsChecksum,
  gOr,
  gOrderDone,
  gOrderNotDone,
  gOtuTableAccess,
  gPlatformSupportsProcessingType,
  gStateDraft,
  gUnclaimed,
  gUnclaimedLab,
  gVisualisationAccess
} from './OrderActionsChecks.ts'
import { DropdownButton } from './DropdownButton.js'
import { gatedActions } from './GatedActions.js'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import type { SampleConfigurationTypeKey } from '../../../../Generated/SampleConfigurationType.js'
import { useModal } from '../../../Modal/UseModal.js'
import { AmbiguousPrimerError, MismatchingAmpliconError, PrimerNotFoundError, validatePrimers } from '../OrderDetail/PrimerValidation.js'
import { errorMessage, infoMessage, successMessage } from '../../../../Model/Message/IMessage.js'
import { isSequencingPlatformUnknown } from '../../../../Util/sequencingPlatform.ts'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import type React from 'react'
import { useEffect, useState } from 'react'
import { getResultUnitLabelPlural } from '../../../../Util/processingType.ts'
import { trackOrderAction, trackSubmitBlocked, trackVizOpened } from '../../../../Services/Telemetry/index.ts'
import { reportPrimerChoice } from '../../../../Services/Telemetry/OrderTelemetry.ts'
import { intermediatesExpired } from '../../../../Util/IntermediatesLifetime.ts'
import { canReprocessDoneOrder } from '../../../../Util/Reprocessing.ts'

export interface OrderContextActionsProps {
  order: IOrder
  samplePools: ISamplePool[]
  orderState: OrderState
  tokenChecksum?: string
  hideDisabled: boolean
}

const safeMessage = (error: unknown) => (error && typeof error === 'object' && 'message' in error ? error.message : 'Unknown error')

export const handleFetchLink = async (orderId: string) => {
  const { data } = await tracingAxios.get<string>(`${Environment.API_URL}/api/orders/${orderId}/share`, { withCredentials: true })
  await navigator.clipboard.writeText(`${window.location.origin}/order/${orderId}?token=${data}`)
}

export const OrderContextActions = (props: OrderContextActionsProps) => {
  const navigate = useNavigate()
  const user = useGetAuthenticatedUser()
  const dispatch = useDispatch()
  const { confirm } = useConfirm()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { openModal } = useModal()
  const expired = intermediatesExpired(useGetIntermediatesStatusSelector(), new Date())
  const awaitsDecision = props.orderState === 'READY_FOR_POSTPROCESSING' || props.orderState === 'PREPROCESSED_BLOCKED'
  const canReprocess = canReprocessDoneOrder(props.orderState, useGetReprocessingGrantSelector(), user?.org?.id, props.order.lab)

  const platformCheck = gPlatformSupportsProcessingType(props, user)
  const canSubmit = !!user && props.orderState === 'READY_FOR_PREPROCESSING' && platformCheck.check()

  function askForPrimer(samplePools: ISamplePool[], error: AmbiguousPrimerError) {
    trackSubmitBlocked('primer_ambiguous', props.order)
    openModal('primerChoice', {
      candidates: error.candidates,
      onChoose: (primer) => {
        reportPrimerChoice(props.order, error.candidates, primer, 'classic')
        void validatePrimersAndShowSubmit(samplePools, primer.id)
      }
    })
  }

  async function validatePrimersAndShowSubmit(samplePools: ISamplePool[], chosenPrimerId?: string) {
    // Primer validation + submit is a LAB-only write: the /pools/{id}/contents endpoint gates on
    // o.lab = org, so a non-lab user would just get an all-null no-op (and previously crashed —
    // SPCFY-FRONTEND-J1). Customers edit metadata via their own path, never primers/submit.
    if (!user || user.org.id !== props.order.lab) {
      trackSubmitBlocked('not_lab', props.order)
      dispatch(addMessage(errorMessage('Only the processing lab can submit this order to the pipeline.')))
      return
    }
    if (!platformCheck.check()) {
      dispatch(addMessage(errorMessage(platformCheck.message)))
      return
    }
    if (isSequencingPlatformUnknown(props.order, samplePools)) {
      trackSubmitBlocked('platform_unknown', props.order)
      dispatch(
        addMessage(infoMessage('None of the files names its sequencing platform. Choose it under "Sequencing platform", then submit again.'))
      )
      openModal('editOrder', { order: props.order })
      return
    }
    {
      const validationMandatory =
        props.order.sampleConfiguration !== 'TRIMMED_SAMPLES' && props.order.sampleConfiguration !== 'MERGED_AND_TRIMMED_SAMPLES'

      if (validationMandatory) {
        /* enforce validation and show suitable error messages */
        try {
          const [updatedSamplePools, primer] = await validatePrimers(props.order, samplePools, chosenPrimerId)
          if (!primer) {
            trackSubmitBlocked('primer_null', props.order)
            dispatch(addMessage(errorMessage('returned Primer is null!')))
            return
          }

          dispatch(
            Actions[orderSlice.name].updatePrimersForSubmitRequest({
              order: props.order,
              primer: primer ?? 'SKIPPED',
              data: updatedSamplePools.flatMap((x) => [...x.samples, ...x.negativeControls])
            })
          )

          dispatch(addMessage(successMessage('Successfully validated the Samples against the Primer Database')))
        } catch (error) {
          if (error instanceof AmbiguousPrimerError) {
            askForPrimer(samplePools, error)
          } else if (error instanceof MismatchingAmpliconError) {
            trackSubmitBlocked('amplicon_mismatch', props.order)
            openModal('primerAmpliconMismatch', { expectedAmplicon: error.expectedAmplicon, foundPrimers: error.foundPrimers })
            dispatch(addMessage(errorMessage('Primer is registered for a different amplicon. ' + error.message)))
          } else if (error instanceof PrimerNotFoundError) {
            trackSubmitBlocked('primer_not_found', props.order)
            openModal('primersNotFound', { amplicon: props.order.targetTaxonomy, primers: error.samplePrimers })
            dispatch(addMessage(errorMessage('Primer not found! ' + error?.message)))
          } else {
            trackSubmitBlocked('validation_error', props.order)
            console.error(error)
            dispatch(addMessage(errorMessage('Sample validation failed! ' + safeMessage(error))))
          }
        }
      } else {
        /*
         * Trimmed orders: primers are already trimmed off the reads, so supplying them is
         * optional. But when the customer DID provide primers, they must still fit the order's
         * target taxonomy — otherwise trimmed mode can be (and has been) abused to submit to the
         * wrong taxonomy. So we only skip validation when no primers were provided at all; any
         * provided primer is enforced exactly like the mandatory (non-trimmed) path.
         */
        const anyPrimerProvided = samplePools.some((pool) =>
          [...(pool.samples ?? []), ...(pool.negativeControls ?? [])].some((s) => !!s.forwardPrimer || !!s.reversePrimer)
        )

        let submitHandled = false
        try {
          const [updatedSamplePools, primer] = await validatePrimers(props.order, samplePools, chosenPrimerId)
          const samplesById = samplePools
            .map((pool) => [...pool.samples, ...pool.negativeControls])
            .flat()
            .reduce((prev, current) => ({ ...prev, [current.id]: current }), {} as Record<string, FileForProcessing>)
          if (primer) {
            submitHandled = true
            dispatch(
              Actions[orderSlice.name].updatePrimersForSubmitRequest({
                order: props.order,
                primer: primer ?? 'SKIPPED',
                data: updatedSamplePools.flatMap((x) =>
                  /* only update items without an already applied minBasePairLength to avoid overwriting customer supplied values */
                  [...x.samples, ...x.negativeControls].filter(
                    (x) => samplesById[x.id].minBasePairLength === null || samplesById[x.id].minBasePairLength === undefined
                  )
                )
              })
            )
            dispatch(addMessage(successMessage('Successfully validated the Samples against the Primer Database')))
          }
        } catch (error) {
          if (anyPrimerProvided) {
            /* provided primers must fit the order taxonomy — block submission, mirror the mandatory path */
            submitHandled = true
            if (error instanceof AmbiguousPrimerError) {
              askForPrimer(samplePools, error)
            } else if (error instanceof MismatchingAmpliconError) {
              trackSubmitBlocked('amplicon_mismatch', props.order)
              openModal('primerAmpliconMismatch', { expectedAmplicon: error.expectedAmplicon, foundPrimers: error.foundPrimers })
              dispatch(addMessage(errorMessage('Primer is registered for a different amplicon. ' + error.message)))
            } else if (error instanceof PrimerNotFoundError) {
              trackSubmitBlocked('primer_not_found', props.order)
              openModal('primersNotFound', { amplicon: props.order.targetTaxonomy, primers: error.samplePrimers })
              dispatch(addMessage(errorMessage('Primer not found! ' + error?.message)))
            } else {
              trackSubmitBlocked('validation_error', props.order)
              console.error(error)
              dispatch(addMessage(errorMessage('Sample validation failed! ' + safeMessage(error))))
            }
          } else {
            dispatch(addMessage(infoMessage('Optional primer validation failed ' + safeMessage(error))))
          }
        }
        if (!submitHandled) {
          openModal('SubmitOrder', {
            order: props.order,
            primer: 'SKIPPED'
          })
        }
      }
    }
  }

  useEffect(() => {
    if (props.tokenChecksum && user && user.org.id == props.order.customer) {
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      window.location.replace(url.toString())
    }
  }, [props.order.customer])

  const handleClaimOrder = (targetTaxa?: Taxas, allowSingletons?: boolean, sampleConfiguration?: SampleConfigurationTypeKey) => {
    dispatch(
      Actions[orderSlice.name].claimOrderRequest({
        orderId: props.order.id,
        token: props.tokenChecksum,
        targetTaxa,
        allowSingletons,
        sampleConfiguration
      })
    )
  }

  // Licence, not feature: these only drive the "your lab shared its licence" badge.
  const directInsightAccess = hasOwnInsightLicense(user)
  const indirectInsightAccess = hasSharedInsightLicense(props.order)

  // const submissionTooltip =
  //   props.order.sampleConfiguration === 'TRIMMED_SAMPLES' || props.order.sampleConfiguration === 'MERGED_AND_TRIMMED_SAMPLES'
  //     ? 'The order is not yet ready to be submitted. Ensure that at least one dataset with samples is present and that all samples have been assigned names'
  //     : 'The order is not yet ready to be submitted. Ensure that at least one dataset with samples is present and that all samples have been assigned names and primers'

  const handleDelete = () => {
    dispatch(Actions[orderSlice.name].deleteOrderRequest({ orderId: props.order.id }))
  }

  const handleFetchLinkAction = async () => {
    try {
      await handleFetchLink(props.order.id)
      dispatch(addMessage(successMessage('Share link copied to clipboard!')))
    } catch (error) {
      console.error('Error fetching link:', error)
      dispatch(addMessage(errorMessage('Failed to copy share link: ' + safeMessage(error))))
    }
  }

  const hasInsightAccess = canUseVisualisationOnOrder(props.order, user)
  const notices = useOrderNotices(props.order.id)

  const groups: Array<{
    label: React.ReactNode
    content: (React.ReactElement | null)[]
    disabled?: boolean
    tooltip?: string
    listClasses?: string
    triggerClasses?: string
  }> = [
    {
      label: 'Sharing & Access',
      content: gatedActions(
        [
          {
            label: 'Claim Order as Lab',
            testId: 'claim-lab',
            onClick: () =>
              openModal('claimAsLab', {
                orderId: props.order.id,
                tokenChecksum: props.tokenChecksum,
                targetTaxa: props.order.targetTaxonomy
              }),
            track: () => trackOrderAction('claim_lab', props.order),
            gates: [gStateDraft, gUnclaimedLab],
            props: props
          },
          {
            label: 'Claim Order as Customer',
            testId: 'claim-customer',
            onClick: () => handleClaimOrder(),
            track: () => trackOrderAction('claim_customer', props.order),
            gates: [gUnclaimed, gOr(gNeedsChecksum, gIsLab)],
            props: props
          },
          {
            label: 'Share Order',
            onClick: handleFetchLinkAction,
            track: () => trackOrderAction('share', props.order),
            gates: [gIsLab],
            props: props
          },
          {
            label: 'Completion Notifications',
            onClick: () => openModal('reportEmail', { order: props.order }),
            track: () => trackOrderAction('completion_notifications', props.order),
            gates: [gIsLab, gOrderNotDone],
            props: props
          },
          {
            label: 'Invite Lab',
            onClick: () => openModal('inviteLab', { orderId: props.order.id }),
            track: () => trackOrderAction('invite_lab', props.order),
            gates: [gStateDraft, gUnclaimedLab, gIsCustomer],
            props: props
          }
        ],
        props.hideDisabled
      )
    },
    {
      label: 'Downloads',
      content: gatedActions(
        [
          {
            label: 'Fastq Files',
            onClick: () => dispatch(Actions[orderSlice.name].getFastqRequest({ orderId: props.order.id })),
            track: () => trackOrderAction('download_fastq', props.order),
            gates: [gIsLabOrCustomer],
            props: props
          },
          {
            label: 'Report',
            onClick: () =>
              dispatch(
                Actions[orderSlice.name].getReportRequest({
                  orderName: props.order.name,
                  orderId: props.order.id,
                  checksum: props.tokenChecksum
                })
              ),
            track: () => trackOrderAction('download_report', props.order),
            noticeTarget: 'REPORT',
            gates: [gOrderDone],
            props: props,
            requiresAuth: false
          },
          {
            label: 'MultiQC report',
            onClick: () => openModal('multiqc', { order: props.order }),
            track: () => trackOrderAction('download_multiqc', props.order),
            testId: 'multiqc-report',
            gates: [gIsLabOrCustomer, gOrderDone],
            props: props
          },
          {
            label: 'OTU.fasta',
            onClick: () =>
              dispatch(Actions[orderSlice.name].downloadOrderArtifactRequest({ orderId: props.order.id, artifact: 'OTU_FASTA' })),
            track: () => trackOrderAction('download_otu_fasta', props.order),
            noticeTarget: 'OTU_FASTA',
            gates: [gIsLabOrCustomer, gOrderDone, gIsMetabarcoding],
            props: props
          },
          {
            label: 'Summary.pdf',
            onClick: () =>
              dispatch(
                Actions[orderSlice.name].downloadOrderArtifactRequest({ orderId: props.order.id, artifact: 'PROCESSING_SUMMARY' })
              ),
            track: () => trackOrderAction('download_summary_pdf', props.order),
            gates: [gIsLabOrCustomer, gOrderDone, gIsEDNA],
            props: props
          }
        ],
        props.hideDisabled,
        notices
      )
    },
    {
      label: 'Manage',
      content: gatedActions(
        [
          {
            label: 'Edit Order',
            onClick: () => openModal('editOrder', { order: props.order }),
            track: () => trackOrderAction('edit_order', props.order),
            gates: [gIsLabOrCustomer],
            props: props
          },
          {
            label: 'Delete Order',
            onClick: () =>
              confirm(
                {
                  title: 'Are you sure you want to Delete this Order?',
                  message: 'This cannot be undone'
                },
                handleDelete
              ),
            track: () => trackOrderAction('delete_order', props.order),
            gates: [gIsLabOrCustomer, gIsUnsubmitted],
            props: props,
            labelClassName: 'text-red-500'
          },
          {
            label: 'Change data for resubmission',
            onClick: () =>
              confirm(
                {
                  title: 'Change Data for resubmission',
                  message: 'This will return the order to an editable state for changing primers etc.'
                },
                () => {
                  dispatch(Actions[orderSlice.name].pullbackOrderRequest({ orderId: props.order.id }))
                }
              ),
            track: () => trackOrderAction('pullback', props.order),
            gates: [gIsLab, gCanBeResubmitted],
            props: props
          },
          {
            label: 'Import Samples',
            onClick: () =>
              openModal('importSamples', {
                order: props.order,
                samplePools: props.samplePools
              }),
            track: () => trackOrderAction('import_samples', props.order),
            gates: [gIsLab, gIsUnsubmitted],
            props: props,
            tooltip: 'Import samples from another order, e.g. to reuse already uploaded files',
            testId: 'import-samples'
          },
          {
            label: 'Manage Files',
            onClick: () => openModal('file', { orderId: props.order.id }),
            track: () => trackOrderAction('manage_files', props.order),
            gates: [gIsLabOrCustomer],
            props: props,
            tooltip: 'View files attached to this order'
          }
        ],
        props.hideDisabled
      )
    },
    {
      label: (
        <>
          <TooltipTrigger
            tooltip={
              user && !directInsightAccess && indirectInsightAccess
                ? 'Your processing lab has shared its INSIGHT license for this order'
                : ''
            }
            disabled={mobileMenuOpen}
          >
            <span style={{ fontFamily: 'Roboto Bold', fontWeight: 'bold', color: '#70d1b8ff' }}>INSIGHT</span>
            {user && !directInsightAccess && indirectInsightAccess && (
              <span className="ml-1 text-xs text-info rounded bg-lime-100">
                <span className={'sm:max-md:hidden'}>🛈</span>
                <span className={'hidden sm:max-md:inline-flex'}>Your processing lab has shared its INSIGHT license for this order</span>
              </span>
            )}
          </TooltipTrigger>
        </>
      ),
      disabled: !hasInsightAccess,
      tooltip: !hasInsightAccess ? 'spcfy INSIGHT is only available on the INSIGHT plan' : undefined,
      listClasses: user && !directInsightAccess && indirectInsightAccess ? 'bg-lime-100' : '',
      triggerClasses: user && !directInsightAccess && indirectInsightAccess ? 'bg-lime-100' : '',
      content: gatedActions(
        [
          {
            label: `View ${getResultUnitLabelPlural(props.order.processingType)}`,
            onClick: () => navigate(`/order/${props.order.id}/otus`),
            track: () => trackVizOpened('otu_table', props.order),
            noticeTarget: 'VISUALISATION',
            gates: [gOrderDone, gIsLabOrCustomer, gOtuTableAccess],
            props: props
          },
          // BETA spike: chat-with-your-data. Only shown to CHAT_BETA-role users; the endpoint gates on the same role.
          ...(Environment.CHAT_ENABLED && isChatBeta(user)
            ? [
                {
                  label: 'Ask AI (beta)',
                  onClick: () => navigate(`/order/${props.order.id}/chat`),
                  gates: [gOrderDone, gIsLabOrCustomer],
                  props: props
                }
              ]
            : []),
          {
            label: 'Show Krona Plot',
            onClick: () => openModal('krona', { order: props.order }),
            track: () => trackVizOpened('krona', props.order),
            noticeTarget: 'VISUALISATION',
            gates: [gOrderDone, gIsLabOrCustomer, gKronaAccess],
            props: props
          },
          {
            label: 'Show Visualisation',
            onClick: () => navigate(`/order/workspace/${props.order.id}`),
            track: () => trackVizOpened('workspace', props.order),
            noticeTarget: 'VISUALISATION',
            gates: [gOrderDone, gIsLabOrCustomer, gVisualisationAccess],
            props: props
          }
        ],
        props.hideDisabled,
        notices
      )
    }
  ]

  return (
    <>
      <div className={'sm:max-md:flex sm:max-md:flex-col sm:max-md:mb-4'}>
        <div className="flex md:flex-wrap gap-2 items-center justify-end">
          {/* Top-priority buttons */}
          <div className="flex flex-wrap items-center sm:max-md:grow">
            {!props.order.orderSubmission && user?.org?.id === props.order.lab && (
              <div
                className={`sm:max-md:grow ${!canSubmit ? 'tooltip tooltip-bottom' : 'tooltip-disabled'}`}
                data-tip={platformCheck.check() ? undefined : platformCheck.message}
              >
                <button
                  data-testid="submit-order"
                  className={`btn btn-sm btn-actionable md:mx-1 md:w-auto sm:w-full md:btn sm:max-md:btn-lg`}
                  onClick={() => {
                    trackOrderAction('submit', props.order)
                    validatePrimersAndShowSubmit(props.samplePools)
                  }}
                  disabled={!canSubmit}
                >
                  <CloudArrowUpIcon className="w-4" /> Submit to Pipeline
                </button>
              </div>
            )}
            {props.order.orderSubmission && (
              <button
                className={`btn btn-sm btn-actionable md:mx-1 sm:max-md:grow md:btn sm:max-md:btn-lg`}
                onClick={() => {
                  trackOrderAction('view_quality', props.order)
                  openModal('pipelineInformation', { order: props.order })
                }}
                disabled={!(user && props.order.orderSubmission)}
              >
                <CloudArrowUpIcon className="w-4" /> Order Quality
              </button>
            )}
            {canReprocess && (
              <div className="sm:max-md:grow">
                <button
                  data-testid="reprocess-order"
                  className="btn btn-sm btn-actionable md:mx-1 md:w-auto sm:w-full md:btn sm:max-md:btn-lg"
                  onClick={() =>
                    confirm(
                      {
                        title: 'Reprocess order',
                        message:
                          'spcfy has enabled reprocessing of this order. This discards the current results: the report, ' +
                          'result table and downloads are unavailable until the rerun has finished. The order returns ' +
                          'to the lab; submit it to the pipeline again afterwards. The rerun is free of charge.'
                      },
                      () => {
                        trackOrderAction('reprocess_order', props.order)
                        dispatch(Actions[orderSlice.name].rerunPreprocessingRequest({ orderId: props.order.id }))
                      }
                    )
                  }
                >
                  <CloudArrowUpIcon className="w-4" /> Reprocess Order
                </button>
              </div>
            )}
            {expired && awaitsDecision && user?.org?.id === props.order.lab && (
              <div className="sm:max-md:grow">
                <button
                  data-testid="rerun-preprocessing"
                  className="btn btn-sm btn-error md:mx-1 md:w-auto sm:w-full md:btn sm:max-md:btn-lg"
                  onClick={() =>
                    confirm(
                      {
                        title: 'Re-run preprocessing',
                        message:
                          'The preprocessing intermediates of this order have been deleted, so postprocessing cannot start. ' +
                          'This discards the current preprocessing results and returns the order to the lab; submit it to the ' +
                          'pipeline again afterwards. Uploaded files are kept, and no credits are charged until postprocessing.'
                      },
                      () => {
                        trackOrderAction('rerun_preprocessing', props.order)
                        dispatch(Actions[orderSlice.name].rerunPreprocessingRequest({ orderId: props.order.id }))
                      }
                    )
                  }
                >
                  <CloudArrowUpIcon className="w-4" /> Re-run Preprocessing
                </button>
              </div>
            )}
            {props.orderState === 'READY_FOR_POSTPROCESSING' && !expired && (
              <div className="sm:max-md:grow">
                <button
                  data-testid="start-postprocessing"
                  className="btn btn-sm btn-primary md:mx-1 md:w-auto sm:w-full md:btn sm:max-md:btn-lg"
                  onClick={() => {
                    trackOrderAction('start_postprocessing', props.order)
                    openModal('startPostprocessing', { order: props.order })
                  }}
                  disabled={!user}
                >
                  <CloudArrowUpIcon className="w-4" /> Start Postprocessing
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 items-end md:w-auto sm:max-md:grow sm:items-center justify-end">
            {/* Desktop view: show grouped dropdowns individually */}
            <div className="hidden md:flex gap-2 flex-wrap">
              {groups.map((group, index) => (
                <DropdownButton
                  key={index}
                  label={group.label}
                  disabled={group.disabled}
                  tooltip={group.tooltip}
                  props={props}
                  triggerClasses={group.triggerClasses}
                  listClasses={group.listClasses}
                >
                  {group.content}
                </DropdownButton>
              ))}
            </div>

            {/* Mobile view: inline expandable accordion */}
            <div className="w-full md:hidden">
              <button className={`btn btn-lg btn-outline w-full`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? 'Hide Actions ☰' : 'More Actions ☰'}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="w-full flex flex-col gap-2 mb-4 border">
            {groups.map((group, idx) => (
              <details key={idx} className={`border border-base-200 rounded-box px-2 py-1`}>
                <summary className={`cursor-pointer font-semibold text-xl text-gray-700 py-1 px-1 ${group.triggerClasses ?? ''}`}>
                  {group.label}
                </summary>
                <ul className={`menu w-full ${group.listClasses}`}>{group.content}</ul>
              </details>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
